import { memo } from 'react';
import type { RefObject } from 'react';
import type { MapRef } from '@vis.gl/react-maplibre';
import { MapZoomControls } from '@/components/ui/MapZoomControls';
import { useZoomControls } from '@/hooks';
import { useMapAreaCenterOffset } from '../hooks/useMapAreaCenterOffset';
import { ZoomReadout } from './ZoomReadout';
import styles from './MapConfigPreview.module.css';

interface MapConfigZoomControlsProps {
  /** Live map reference (stable across panel resizes). */
  mapRef: RefObject<MapRef | null>;
  /** Whether the map has finished loading. */
  isMapReady: boolean;
  /** Minimum reachable zoom. */
  minZoom: number;
  /** Maximum reachable zoom. */
  maxZoom: number;
}

/**
 * Glass horizontal zoom pill, centered in the map area to the left of the open
 * SlidePanel — mirroring the main map's MapControlsToolbar. Bundles the zoom
 * in/out buttons with a live current-zoom readout (the memoized `ZoomReadout`
 * leaf owns the high-frequency `'zoom'` subscription) into a single pill, so the
 * two never overlap on mobile. Isolated from {@link MapConfigPreview} so the
 * panel-width subscription (via the shared `useMapAreaCenterOffset`) never
 * re-renders the underlying `<Map>`.
 */
export const MapConfigZoomControls = memo(function MapConfigZoomControls({
  mapRef,
  isMapReady,
  minZoom,
  maxZoom,
}: MapConfigZoomControlsProps) {
  const { isZoomInDisabled, isZoomOutDisabled, handleZoomIn, handleZoomOut } =
    useZoomControls({ mapRef, isMapReady, minZoom, maxZoom });
  const offsetStyle = useMapAreaCenterOffset();

  return (
    <div className={styles.zoomPill} style={offsetStyle}>
      <MapZoomControls
        orientation="horizontal"
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        isZoomInDisabled={isZoomInDisabled}
        isZoomOutDisabled={isZoomOutDisabled}
      />
      <ZoomReadout
        mapRef={mapRef}
        isMapReady={isMapReady}
        minZoom={minZoom}
        maxZoom={maxZoom}
      />
    </div>
  );
});
